"use client";

import { useState, useEffect } from "react";
import {
  Lightbulb,
  MessageSquareText,
  ClipboardList,
  Target,
  Sparkles,
  BookOpen,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
} from "@/components/ui/sheet";
import { trackEvent, AnalyticsEvents } from "@/lib/analytics";

interface TipItem {
  icon: React.ReactNode;
  title: string;
  description: string;
  examples?: string[];
}

const tips: TipItem[] = [
  {
    icon: <ClipboardList className="h-4 w-4" />,
    title: "Log sleep consistently",
    description:
      "The more sleep data you log, the better Lullabai can understand your child's patterns. Best results are seen with at least 3 full days of sleep logs.",
  },
  {
    icon: <MessageSquareText className="h-4 w-4" />,
    title: "Be specific in your questions",
    description:
      "Instead of asking general questions, give the coach context about what's happening. The more detail you share, the more tailored the advice.",
    examples: [
      '"My daughter is waking at 5am — how can I extend morning sleep?"',
      '"She fights her second nap every day — what should I try?"',
    ],
  },
  {
    icon: <Target className="h-4 w-4" />,
    title: "Set your sleep goals",
    description:
      "Use the Goals tab to set a target bedtime, wake time, and nap schedule. The coach will use these to give advice aligned with what you're working toward.",
  },
  {
    icon: <Sparkles className="h-4 w-4" />,
    title: "Try the suggested prompts",
    description:
      "Not sure what to ask? Use the suggestions below the chat. They're tailored to your child's data and can surface insights you might not think to ask about.",
  },
  {
    icon: <BookOpen className="h-4 w-4" />,
    title: "Bookmark useful advice",
    description:
      "Tap any message with the coach to reveal the bookmark icon. Save tips you want to revisit later — they'll be available from the bookmark button in the chat bar.",
  },
];

// LocalStorage key for tracking whether the user has seen the tips
const TIPS_SEEN_KEY = "lullabai-coach-tips-seen";

export function CoachTipsSheet() {
  const [isOpen, setIsOpen] = useState(false);
  const [showPulse, setShowPulse] = useState(false);

  // Check if user has seen the tips before
  useEffect(() => {
    const hasSeen = localStorage.getItem(TIPS_SEEN_KEY);
    if (!hasSeen) {
      setShowPulse(true);
    }
  }, []);

  // Mark tips as seen when opened
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      trackEvent(AnalyticsEvents.COACH_TIPS_OPENED);
      if (showPulse) {
        localStorage.setItem(TIPS_SEEN_KEY, "true");
        setShowPulse(false);
      }
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative"
          aria-label="Tips for using the AI coach"
          id="coach-tips-button"
        >
          <Lightbulb className="h-4 w-4" />
          {/* Attention dot for first-time users */}
          {showPulse && (
            <span className="absolute -top-1 -right-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500" />
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col pt-[env(safe-area-inset-top)]"
      >
        <SheetHeader className="p-4 pb-3 border-b">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Lightbulb className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <SheetTitle>Get the Most from Lullabai</SheetTitle>
              <SheetDescription className="text-xs">
                Tips for better sleep advice
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="p-4 space-y-3">
            {/* Intro blurb */}
            <div className="rounded-xl bg-gradient-to-br from-primary/5 to-amber-50 dark:from-primary/10 dark:to-amber-950/20 border border-primary/10 p-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                The Lullabai coach works best when it has context. Here are
                some tips to help you get the most personalised and helpful
                advice.
              </p>
            </div>

            {/* Tips list */}
            {tips.map((tip, index) => (
              <TipCard key={index} tip={tip} />
            ))}

            {/* Footer note */}
            <div className="text-center pt-2 pb-4">
              <p className="text-xs text-muted-foreground/60">
                The more you use the coach, the better it gets at
                understanding your child's needs 💛
              </p>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

function TipCard({ tip }: { tip: TipItem }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasExamples = tip.examples && tip.examples.length > 0;

  return (
    <div
      className={`
        group rounded-xl border bg-card transition-all duration-200 overflow-hidden
        ${isExpanded
          ? "ring-1 ring-primary/15 shadow-md border-primary/15"
          : "hover:border-primary/20 hover:shadow-sm border-border/60 shadow-sm"
        }
      `}
    >
      <div
        className={`p-4 ${hasExamples ? "cursor-pointer" : ""}`}
        onClick={() => hasExamples && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start gap-3">
          <div
            className={`
              mt-0.5 h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors
              ${isExpanded
                ? "bg-primary/15 text-primary"
                : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
              }
            `}
          >
            {tip.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-card-foreground">
                {tip.title}
              </h3>
              {hasExamples && (
                <ChevronRight
                  className={`h-3.5 w-3.5 text-muted-foreground/40 flex-shrink-0 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""
                    }`}
                />
              )}
            </div>
            <p className="text-xs text-muted-foreground/80 leading-relaxed mt-1">
              {tip.description}
            </p>
          </div>
        </div>
      </div>

      {/* Expandable examples section */}
      {hasExamples && (
        <div
          className={`grid transition-all duration-200 ease-out ${isExpanded
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0"
            }`}
        >
          <div className="overflow-hidden">
            <div className="px-4 pb-4 pt-0 pl-14 space-y-2">
              <div className="text-[11px] font-medium text-muted-foreground/60 uppercase tracking-wider">
                Examples
              </div>
              {tip.examples!.map((example, i) => (
                <div
                  key={i}
                  className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2 leading-relaxed italic"
                >
                  {example}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
